import { Button, Form, Input, Select, Tooltip } from 'antd';
import { ThunderboltOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router';
import { useQueryParameters } from '../../hooks';
import { useGetBuildingsQuery } from '../../redux/api/location';

export const SalesOrderFilter = ({
    showSearchInput = true,
    showProductionMethod = true,
    showBuilding = false,
    showFastlanePreset = false,
}: {
    showSearchInput?: boolean;
    showProductionMethod?: boolean;
    showBuilding?: boolean;
    showFastlanePreset?: boolean;
}) => {
	const queryParams = useQueryParameters();
	const navigate = useNavigate();
	const [form] = Form.useForm();
	const { data: buildings = [] } = useGetBuildingsQuery();
	const hasMultipleBuildings = buildings.length > 1;

	// When there is no text search input, selects apply immediately on change.
	// When search is present, everything waits for the Filter button.
	const applyImmediately = !showSearchInput;

	const applyParam = (key: string, value: string | undefined) => {
		const params = new URLSearchParams(queryParams);
		params.set('page', '1');
		if (value) {
			params.set(key, value);
		} else {
			params.delete(key);
		}
		navigate({ search: params.toString() });
	};

	const onFinish = (values: { search: string; production_method: string; building?: string }) => {
		const params = new URLSearchParams(queryParams);
		params.set('page', '1');
		if (values.search) {
			params.set('search', values.search);
		}
		if (values.production_method) {
			params.set('production_method', values.production_method);
		} else {
			params.delete('production_method');
		}
		if (values.building) {
			params.set('building', values.building);
		} else {
			params.delete('building');
		}
		navigate({ search: params.toString() });
	};

	const handleReset = () => {
		const params = new URLSearchParams(queryParams);
		if (showSearchInput) {
			form.setFieldValue('search', '');
			params.delete('search');
		}
		if (showProductionMethod) {
			form.setFieldValue('production_method', undefined);
			params.delete('production_method');
		}
		if (showBuilding) {
			form.setFieldValue('building', undefined);
			params.delete('building');
		}
		params.set('page', '1');
		navigate({ search: params.toString() });
	};

	// Fastlane preset: clear all current filters/sort and apply the Fastlane queue view.
	const handleFastlane = () => {
		const params = new URLSearchParams();
		params.set('status', 'Received,In Progress,Ready For PBP');
		params.set('production_method', 'Fastlane');
		params.set('ordering', 'required_partner_ship_date');
		params.set('page', '1');
		if (showSearchInput) form.setFieldValue('search', '');
		if (showProductionMethod) form.setFieldValue('production_method', 'Fastlane');
		if (showBuilding) form.setFieldValue('building', undefined);
		navigate({ search: params.toString() });
	};

	return (
		<Form
			name="order-filter"
			form={form}
			layout="horizontal"
			initialValues={{ remember: true }}
			onFinish={onFinish}
			autoComplete="off"
		>
			<div className="flex flex-wrap items-center gap-2">
				{showSearchInput && (
					<Form.Item
						name="search"
						initialValue={queryParams.get('search')}
						style={{ margin: 0 }}
					>
						<Input placeholder="Search" aria-label="Search" allowClear style={{ width: 300 }} />
					</Form.Item>
				)}

				{showProductionMethod && (
					<Form.Item
						name="production_method"
						initialValue={queryParams.get('production_method') || undefined}
						style={{ margin: 0 }}
					>
						<Select
							allowClear
							placeholder="Method"
							aria-label="Method"
							style={{ width: 140 }}
							onChange={applyImmediately ? (val) => applyParam('production_method', val) : undefined}
						>
							<Select.Option value="Fastlane">Fastlane</Select.Option>
							<Select.Option value="QuickTurn">QuickTurn</Select.Option>
							<Select.Option value="Swiss">Swiss</Select.Option>
							<Select.Option value="Traditional">Traditional</Select.Option>
							<Select.Option value="No">No</Select.Option>
						</Select>
					</Form.Item>
				)}

				{showBuilding && hasMultipleBuildings && (
					<Form.Item
						label="Building"
						name="building"
						initialValue={queryParams.get('building') || undefined}
						style={{ margin: 0 }}
					>
						<Select
							allowClear
							placeholder="All"
							style={{ width: 160 }}
							onChange={applyImmediately ? (val) => applyParam('building', val) : undefined}
						>
							<Select.Option value="__none__">— No Building —</Select.Option>
							{buildings.map((b) => (
								<Select.Option key={b.id} value={b.id}>
									{b.name}
								</Select.Option>
							))}
						</Select>
					</Form.Item>
				)}

				<Form.Item noStyle>
					<div className="flex gap-2">
						<Button onClick={handleReset}>Reset</Button>
						{!applyImmediately && (
							<Button type="primary" htmlType="submit">
								Filter
							</Button>
						)}
						{showFastlanePreset && (
							<Tooltip title="Show the Fastlane queue — Received / In Progress / Ready for PBP, Method = Fastlane, sorted by Required Ship Date">
								<Button
									icon={<ThunderboltOutlined />}
									onClick={handleFastlane}
									style={{ color: '#52c41a', borderColor: '#52c41a' }}
								>
									Fastlane
								</Button>
							</Tooltip>
						)}
					</div>
				</Form.Item>
			</div>
		</Form>
	);
};
